<template>
  <q-dialog v-model="localIsOpen" @hide="closeDialog">
    <q-card style="width: 700px; max-width: 80vw">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          {{ $t('events.sponsorAssets.title') }} - {{ this.event.name }}
        </div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>

      <q-card-section>
        <p class="text-body2">{{ $t('events.sponsorAssets.description') }}</p>

        <q-banner v-if="!canUpload" class="bg-warning text-white q-mb-md">
          {{ $t('events.sponsorAssets.uploadRestrictedDate') }}
        </q-banner>

        <q-form
          v-if="canUpload"
          ref="assetForm"
          @submit.prevent="handleUpload"
          class="q-mb-md"
        >
          <div class="row q-col-gutter-sm items-start">
            <div class="col-grow">
              <q-file
                v-model="newAsset"
                :label="$t('events.sponsorAssets.uploadLabel')"
                accept=".jpg, .jpeg, .png, .pdf"
                max-file-size="5242880"
                outlined
                dense
                @rejected="onFileRejected"
                :disable="!canUpload"
              >
                <template v-slot:prepend>
                  <q-icon name="attach_file" />
                </template>
              </q-file>
            </div>
            <div class="col-grow">
              <q-input
                v-model="assetName"
                :label="$t('events.sponsorAssets.assetName')"
                outlined
                dense
                lazy-rules="ondemand"
                counter
                maxlength="18"
                :rules="[(val) => !!val || $t('validation.required')]"
                :disable="!canUpload"
              />
            </div>
            <div class="col-auto">
              <q-btn
                type="submit"
                color="primary"
                :label="$t('common.save')"
                :loading="uploading"
                :disable="!newAsset || !assetName || !canUpload"
              />
            </div>
          </div>
        </q-form>

        <q-separator class="q-my-md" />

        <div v-if="loadingAssets" class="text-center q-pa-md">
          <q-spinner color="primary" size="2em" />
        </div>
        <div v-else-if="assets && assets.length > 0">
          <q-list bordered separator>
            <q-item v-for="asset in assets" :key="asset.id">
              <q-item-section avatar>
                <q-img
                  :src="asset.url"
                  style="width: 50px; height: 50px"
                  fit="cover"
                  class="rounded-borders"
                >
                  <template v-slot:error>
                    <div
                      class="absolute-full flex flex-center bg-negative text-white"
                    >
                      <q-icon name="broken_image" />
                    </div>
                  </template>
                </q-img>
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ asset.name }}</q-item-label>
                <q-item-label caption>
                  {{ formatDate(asset.created_at) }}
                </q-item-label>
              </q-item-section>
              <q-item-section v-if="canUpload" side>
                <div class="row q-gutter-xs">
                  <q-btn
                    icon="delete"
                    flat
                    dense
                    color="negative"
                    @click="handleDelete(asset.id)"
                    :loading="deletingId === asset.id"
                  >
                    <q-tooltip>{{ $t('events.deleteTooltip') }}</q-tooltip>
                  </q-btn>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
        </div>
        <div v-else class="text-center text-grey q-pa-md">
          {{ $t('events.sponsorAssets.noAssets') }}
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { Notify, date } from 'quasar';
import { handleFileRejected } from '@/helpers/fileHelper';

export default {
  name: 'SponsorAssetsDialog',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localIsOpen: this.isOpen,
      newAsset: null,
      assetName: '',
      uploading: false,
      loadingAssets: false,
      deletingId: null,
    };
  },
  computed: {
    ...mapState('dashboardEvents', {
      assets: (state) => state.sponsorAssets,
    }),
    canUpload() {
      if (!this.event.startDate) {
        return true;
      }

      const today = date.formatDate(new Date(), 'YYYY-MM-DD');

      return today <= this.event.startDate;
    },
  },
  watch: {
    isOpen: {
      handler(val) {
        this.localIsOpen = val;
        if (val) {
          this.fetchAssets();
        }
      },
      immediate: true,
    },
    localIsOpen(val) {
      this.$emit('update:isOpen', val);
    },
  },
  methods: {
    ...mapActions('dashboardEvents', [
      'fetchSponsorAssets',
      'updateSponsorAssets',
      'deleteSponsorAsset',
    ]),
    formatDate(dateStr) {
      return date.formatDate(dateStr, 'YYYY-MM-DD HH:mm');
    },
    async fetchAssets() {
      this.loadingAssets = true;
      try {
        await this.fetchSponsorAssets(this.event.id);
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: this.$t('error.server'),
          position: 'bottom',
        });
      } finally {
        this.loadingAssets = false;
      }
    },
    async handleUpload() {
      if (!this.newAsset || !this.assetName) return;

      this.uploading = true;
      try {
        const formData = new FormData();
        formData.append('image', this.newAsset);
        formData.append('name', this.assetName);
        formData.append('wp_run_id', this.event.id);

        await this.updateSponsorAssets({
          eventId: this.event.id,
          data: formData,
        });

        this.newAsset = null;
        this.assetName = '';
        this.$nextTick(() => {
          this.$refs.assetForm?.resetValidation();
        });
        Notify.create({
          type: 'positive',
          message: this.$t('common.added'),
          position: 'bottom',
        });
      } catch (error) {
        const errorMessage = error.response?.data?.message === 'events.sponsorAssets.duplicate'
            ? this.$t('events.sponsorAssets.duplicate')
            : this.$t('error.server');
            
        Notify.create({
          type: 'negative',
          message: errorMessage,
          position: 'bottom',
        });
      } finally {
        this.uploading = false;
      }
    },
    async handleDelete(assetId) {
      this.deletingId = assetId;
      try {
        await this.deleteSponsorAsset({
          assetId: assetId,
          eventId: this.event.id,
        });
        Notify.create({
          type: 'positive',
          message: this.$t('common.deleted'),
          position: 'bottom',
        });
      } catch (error) {
        Notify.create({
          type: 'negative',
          message: this.$t('error.server'),
          position: 'bottom',
        });
      } finally {
        this.deletingId = null;
      }
    },
    closeDialog() {
      this.newAsset = null;
      this.assetName = '';
      this.$nextTick(() => {
        this.$refs.assetForm?.resetValidation();
      });
      this.localIsOpen = false;
      this.$emit('close');
    },
    onFileRejected(rejectedEntries) {
      handleFileRejected(rejectedEntries, this.$t);
    },
  },
};
</script>
